// Global Instructions Rule Applied!
//
// The read-only file list: files the user may open but not add, rename or
// remove - a locked campaign's attachments, the files an account receives
// with a campaign. FileUploadManager has no read-only mode (its list is its
// upload surface), so this is the house component wherever stored files are
// only shown.
//
// An antd Table size='small' (the read-only table of
// docs/FRONTEND_DESIGN_PATTERNS.md) with File, Size (when any file's size is
// known) and one action per file. View opens the file in a viewer modal
// above whatever the list sits in (a drawer, a modal), so closing it returns
// the user to where they were with nothing lost. A spreadsheet gets Download
// instead: the Office online viewer cannot fetch a stored file (the signed
// link is short-lived, and unreachable from a local stack), so the file is
// saved under its own name and opened in Excel. It renders no wrapper of its
// own: the caller places it inside its section, card or modal.

import React, { useCallback, useMemo, useState } from 'react'
import { Button, Modal, Table, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { getAccessToken } from '../auth/authService'
import { getSignedUrl } from '../services/filesService.ts'
import { showErrorToast } from '../utils/errorToast.tsx'
import { bytesText } from '../app-layout/mobile/lib/format.ts'
import { store } from '../../state_manager/store.ts'
import { OFFICE_EXTENSIONS, OFFICE_VIEWER_URL } from '../../state_manager/constants/files.ts'
import { saveBlob } from '../../app_modules/document_reports/services/documentReportsService.ts'
import FileViewer from './FileViewer/index.tsx'

const { Text } = Typography

export interface ReadOnlyFile {
  id: string
  /** The name the user sees (file_name, spaces and punctuation preserved). */
  name: string
  /** The Storage path (file_url), or the link of a 'url' attachment. */
  path: string
  /** An unknown size shows as a dash; with no size at all the column goes. */
  sizeBytes?: number | null
}

interface ReadOnlyFileListProps {
  files: ReadOnlyFile[]
  loading?: boolean
  /** Shown instead of the table's default empty state. */
  emptyText?: React.ReactNode
}

interface ViewedFile {
  name: string
  url: string
}

const isLink = (path: string): boolean => /^https?:\/\//i.test(path)

const isOfficeFile = (name: string): boolean => {
  const extension = name.toLowerCase().split('.').pop()
  return extension ? OFFICE_EXTENSIONS.includes(extension) : false
}

/** A stored spreadsheet is downloaded, not viewed. A link attachment keeps
 *  View whatever its name: there is no stored file to save. */
const isDownloadOnly = (file: ReadOnlyFile): boolean =>
  !isLink(file.path) && /\.(xlsx|xlsm|xls)$/i.test(file.name)

export default function ReadOnlyFileList({
  files,
  loading = false,
  emptyText = 'No files'
}: ReadOnlyFileListProps): React.ReactElement {
  const [openingId, setOpeningId] = useState<string | null>(null)
  const [viewed, setViewed] = useState<ViewedFile | null>(null)

  const handleView = useCallback(async (file: ReadOnlyFile) => {
    setOpeningId(file.id)
    try {
      // a 'url' attachment is an external link, not a Storage path
      const url = isLink(file.path)
        ? file.path
        : (await getSignedUrl(file.path, getAccessToken(store.getState().auth))).signed_url
      setViewed({
        name: file.name,
        url: isOfficeFile(file.name) ? `${OFFICE_VIEWER_URL}${encodeURIComponent(url)}` : url
      })
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to open the file')
    } finally {
      setOpeningId(null)
    }
  }, [])

  const handleDownload = useCallback(async (file: ReadOnlyFile) => {
    setOpeningId(file.id)
    try {
      const signed = await getSignedUrl(file.path, getAccessToken(store.getState().auth))
      const response = await fetch(signed.signed_url)
      if (!response.ok) throw new Error(`The stored file could not be downloaded (${response.status})`)
      saveBlob(await response.blob(), file.name)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : `Failed to download ${file.name}`)
    } finally {
      setOpeningId(null)
    }
  }, [])

  // A list whose source carries no sizes at all shows no Size column,
  // rather than a column of dashes.
  const showSize = useMemo(() => files.some((f) => f.sizeBytes != null), [files])

  const columns = useMemo(
    (): ColumnsType<ReadOnlyFile> => [
      { title: 'File', dataIndex: 'name', key: 'name' },
      ...(showSize
        ? [
            {
              title: 'Size',
              key: 'size',
              width: 100,
              align: 'right' as const,
              render: (_: unknown, file: ReadOnlyFile) =>
                bytesText(file.sizeBytes) ?? <Text type='secondary'>—</Text>
            }
          ]
        : []),
      {
        title: '',
        key: 'actions',
        width: 100,
        align: 'right',
        render: (_: unknown, file) =>
          isDownloadOnly(file) ? (
            <Button size='small' loading={openingId === file.id} onClick={() => handleDownload(file)}>
              Download
            </Button>
          ) : (
            <Button size='small' loading={openingId === file.id} onClick={() => handleView(file)}>
              View
            </Button>
          )
      }
    ],
    [handleDownload, handleView, openingId, showSize]
  )

  return (
    <>
      <Table<ReadOnlyFile>
        rowKey='id'
        size='small'
        columns={columns}
        dataSource={files}
        loading={loading}
        pagination={false}
        locale={{ emptyText }}
      />
      <Modal
        title={viewed?.name}
        open={viewed !== null}
        onCancel={() => setViewed(null)}
        footer={null}
        width='80vw'
        destroyOnHidden
      >
        <div style={{ height: '75vh' }}>
          <FileViewer fileUrl={viewed?.url} fileName={viewed?.name} />
        </div>
      </Modal>
    </>
  )
}
